import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Shared layout for short legal/policy pages: Accessibility Statement,
 * Privacy Policy, Terms of Service. Phase 3 keeps these brief per spec.
 */
export default function LegalPage({ title, lead, sections, slug }) {
    useEffect(() => { document.title = `${title} — AccessNova`; }, [title]);

    return (
        <article className="bg-white">
            <header className="border-b border-brand-border">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16">
                    <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Legal</p>
                    <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-brand-text tracking-tight">{title}</h1>
                    <p className="mt-5 text-lg text-brand-body">{lead}</p>
                    <p className="mt-3 text-xs text-brand-muted">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
                </div>
            </header>

            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
                <div className="space-y-10">
                    {sections.map((s, i) => (
                        <section key={i} aria-labelledby={`sec-${i}`} data-testid={`legal-section-${i}`}>
                            <h2 id={`sec-${i}`} className="text-2xl font-bold text-brand-text">{s.heading}</h2>
                            <div className="mt-3 space-y-3 text-base text-brand-body leading-relaxed">
                                {s.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
                                {s.list && (
                                    <ul className="list-disc list-outside pl-5 space-y-1.5">
                                        {s.list.map((l, k) => <li key={k}>{l}</li>)}
                                    </ul>
                                )}
                            </div>
                        </section>
                    ))}
                </div>

                <div className="mt-16 pt-8 border-t border-brand-border">
                    <p className="text-sm text-brand-body">
                        Questions about this {title.toLowerCase()}? Email{" "}
                        <a href="mailto:hello@accessnova.co" className="text-brand-teal underline hover:text-brand-tealBright">hello@accessnova.co</a>.
                    </p>
                    <Link to="/contact"
                          className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-navy text-white font-semibold px-6 py-3 hover:bg-brand-teal transition-colors duration-200 touch-target"
                          data-testid={`legal-${slug}-contact`}>
                        Contact us <ArrowRight size={18} aria-hidden="true" />
                    </Link>
                </div>
            </div>
        </article>
    );
}
